import { Button } from '@creditornot/cb-button';
import { cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { InputAlert } from '@creditornot/cb-input';
import { useMutation } from '@tanstack/react-query';
import React, { useContext, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { createOptionBinding, updateOptionBindingsOrder } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Item, OptionBinding } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { isDefined } from 'common/utils';
import { parseApiError } from 'modules/access-control/utils';
import { MenuPermissionsContext } from 'modules/menu/components/MenuPermissionsContext';
import useMenuData from 'modules/menu/useMenuData';

import AddOptionsToItem from './AddOptionsToItem';
import { OptionBindings } from './OptionBindings';
import { buildOptionBindingCreatePayload } from '../OptionBinding/utils';

type Props = {
  className?: string;
  item: Item;
  isWeightedItem: boolean;
};

const Root = styled.div`
  border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  margin-block-end: 32px;
`;

const Header = styled.div`
  ${typographyCss.Title1()};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;
  border-block-end: 1px solid ${cbSemanticCssVars.borderPrimary};
`;

const StyledInputAlert = styled(InputAlert)`
  margin: 26px 0;
`;

const OptionBindingsView = ({ className, item, isWeightedItem }: Props) => {
  const intl = useIntl();

  const menuPermissions = useContext(MenuPermissionsContext);

  const { options, updateMenuData } = useMenuData();

  const [isItemOptionsModalOpen, setIsItemOptionsModalOpen] = useState(false);

  const getOptionFromOptionId = (optionId: string) => options.find(option => option.id === optionId);

  const addOptionBindings = useMutation(async (selectedOptionIds: Set<string>) => {
    const addedOptionBindings = [...selectedOptionIds]
      .map(id => {
        const option = getOptionFromOptionId(id);
        return option ? buildOptionBindingCreatePayload(option) : undefined;
      })
      .filter(isDefined);

    let updatedItem: Item | undefined;
    for (const payload of addedOptionBindings) {
      updatedItem = await createOptionBinding(item.menu_id, item.id, payload);
    }
    if (updatedItem) {
      updateMenuData({ actionType: 'updateItem', payload: updatedItem });
    }
    setIsItemOptionsModalOpen(false);
  });

  const { mutateAsync: updateOptionBindings } = useMutation(
    (optionBindings: OptionBinding[]) =>
      updateOptionBindingsOrder(
        item.menu_id,
        item.id,
        optionBindings.map(optionBinding => optionBinding.id),
      ),
    {
      onSuccess: updatedItem => {
        updateMenuData({ actionType: 'updateItem', payload: updatedItem });
      },
      onError: (error: any) => {
        toast.error(<FormattedMessage id={parseApiError(error)} />, { duration: Infinity });
      },
    },
  );

  return (
    <Root className={className}>
      <Header>
        <FormattedMessage id="menu-item.options" />
        {!isWeightedItem && menuPermissions.contentEdit && (
          <Button variant="secondary" size="small" onClick={() => setIsItemOptionsModalOpen(true)}>
            <FormattedMessage id="menu-item.add-option" />
          </Button>
        )}
      </Header>

      {isWeightedItem && (
        <StyledInputAlert variant="info">
          {intl.formatMessage({ id: 'menu-item.disabled-for-weighted-items' })}
        </StyledInputAlert>
      )}

      {!isWeightedItem && (
        <OptionBindings
          item={item}
          optionBindings={item.option_bindings}
          canDrag={menuPermissions.contentEdit}
          onUpdateOptionBindings={updateOptionBindings}
        />
      )}

      {!isWeightedItem && !item.option_bindings.length && (
        <StyledInputAlert variant="info">{intl.formatMessage({ id: 'menu-item.no-options' })}</StyledInputAlert>
      )}

      <AddOptionsToItem
        isOpen={isItemOptionsModalOpen}
        loading={addOptionBindings.isLoading}
        onClose={() => setIsItemOptionsModalOpen(false)}
        onAddOptionBindings={addOptionBindings.mutate}
      />
    </Root>
  );
};

export default OptionBindingsView;
